import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CardMedia, Dialog, DialogContent } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { StyledCard, StyledCardsContainer, StyledCardText, StyledGalleryContainer, StyledGalleryTitle, StyledGalleryWrapper, StyledLeftArrowIconButton, StyledRightArrowIconButton } from '../../../../styles/Homepage/GallerySection.style';

interface CardProps {
    name: string;
    image: string;
}

interface GalleryProps {
    title: string;
    cards: CardProps[];
}

const GallerySection: React.FC<GalleryProps> = ({ title, cards }) => {
    const galleryRef = useRef<HTMLDivElement>(null);
    const currentIndex = useRef<number>(0);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [paused, setPaused] = useState<boolean>(false);

    const scrollToIndex = (index: number) => {
        const gallery = galleryRef.current;
        if (!gallery || gallery.children.length === 0) return;

        const card = gallery.children[0] as HTMLElement;
        const cardWidth = card.offsetWidth + 20; // width + margin
        gallery.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
    };

    const scrollGallery = useCallback((direction: 'left' | 'right') => {
        const gallery = galleryRef.current;
        if (!gallery) return;

        const totalCards = gallery.children.length;
        if (totalCards === 0) return;

        if (direction === 'left') {
            currentIndex.current = Math.max(0, currentIndex.current - 1);
        } else {
            currentIndex.current = currentIndex.current + 1;
            if (currentIndex.current >= totalCards) {
                currentIndex.current = 0;
            }
        }
        scrollToIndex(currentIndex.current);
    }, []);

    useEffect(() => {
        if (paused || modalOpen) return;

        const intervalId = setInterval(() => scrollGallery('right'), 3000);

        return () => {
            clearInterval(intervalId);
        };
    }, [cards.length, scrollGallery, paused, modalOpen]);

    const handleImageClick = (image: string) => {
        setSelectedImage(image);
        setModalOpen(true);
    };

    return (
        <>
            <StyledGalleryContainer>
                <StyledGalleryTitle>{title}</StyledGalleryTitle>
                <StyledGalleryWrapper
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocus={() => setPaused(true)}
                    onBlur={() => setPaused(false)}
                >
                    {/* Left Arrow */}
                    <StyledLeftArrowIconButton
                        onClick={() => scrollGallery('left')}
                        aria-label="Scroll left"
                    >
                        <ArrowBackIos />
                    </StyledLeftArrowIconButton>

                    {/* Cards Container */}
                    <StyledCardsContainer ref={galleryRef}>
                        {cards.map((card, index) => (
                            <StyledCard
                                key={index}
                                onClick={() => handleImageClick(card.image)}
                            >
                                <CardMedia component="img" src={card.image} alt={card.name} />
                                <StyledCardText>{card.name}</StyledCardText>
                            </StyledCard>
                        ))}
                    </StyledCardsContainer>

                    {/* Right Arrow */}
                    <StyledRightArrowIconButton
                        onClick={() => scrollGallery('right')}
                        aria-label="Scroll right"
                    >
                        <ArrowForwardIos />
                    </StyledRightArrowIconButton>
                </StyledGalleryWrapper>
            </StyledGalleryContainer>

            {/* Modal pour afficher l'image en grand */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
                <DialogContent>
                    {selectedImage && (
                        <img
                            src={selectedImage}
                            alt="Full view"
                            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default GallerySection;
